/**
 * Sends signing drafts one at a time, always reading the latest values at the
 * moment of the request. Finish waits for that chain so an older save cannot
 * still be in flight when the signature is committed.
 */
export interface DraftSnapshot<T> {
  serialized: string
  payload: T
}

export class DraftAutosave<T> {
  private chain: Promise<void> = Promise.resolve()
  private held = false
  private lastSaved = ''

  constructor(
    private save: (payload: T) => Promise<void>,
    private onError?: (error: Error) => void,
  ) {}

  /** Replaces the request used for the next save. An in-flight request keeps the one it started with. */
  setSave(save: (payload: T) => Promise<void>, onError?: (error: Error) => void): void {
    this.save = save
    this.onError = onError
  }

  get isHeld(): boolean {
    return this.held
  }

  /** The last payload that reached the server, so an unchanged form is not sent again. */
  savedSerialized(): string {
    return this.lastSaved
  }

  /** A draft just loaded from the server is already saved. */
  noteSaved(serialized: string): void {
    this.lastSaved = serialized
  }

  /** Queue a save of whatever `read` returns when the queue reaches it. */
  enqueue(read: () => DraftSnapshot<T> | null): void {
    if (this.held) return
    this.chain = this.chain.then(() => this.run(read))
  }

  /**
   * Stop new saves, wait for the one in flight, then write whatever is current.
   * A failure leaves the payload unsent so the caller can retry or submit it itself.
   */
  async flush(read: () => DraftSnapshot<T> | null): Promise<void> {
    this.held = true
    await this.chain.catch(() => undefined)
    await this.drain(read)
  }

  /** Keeps the queue alive when a save fails, and tells the page once it can show it. */
  private async run(read: () => DraftSnapshot<T> | null): Promise<void> {
    try {
      await this.drain(read)
    } catch (error) {
      if (error instanceof Error) this.onError?.(error)
    }
  }

  private async drain(read: () => DraftSnapshot<T> | null): Promise<void> {
    // A save that loses the race to the next keystroke loops and sends that
    // newer value. The cap keeps a `read` that never settles from spinning.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const next = read()
      if (!next || next.serialized === this.lastSaved) return
      await this.save(next.payload)
      this.lastSaved = next.serialized
    }
  }
}
